import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";

const BREADCRUMBS = [
  { name: "Home", url: "/" },
  { name: "Services", url: "/services" }
];

const ServiceCard = ({ service, reverse }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}>
    <div className={reverse ? "lg:order-2" : ""}>
      <div className="relative">
        <img src={service.image} alt={service.title} className="rounded-lg shadow-2xl object-cover w-full h-[400px]" />
        <div className="absolute -bottom-4 -right-4 bg-amber-700 text-white px-6 py-3 rounded-lg font-bold">
          {service.price}
        </div>
      </div>
    </div>
    <div className={`space-y-6 ${reverse ? "lg:order-1" : ""}`}>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{service.title}</h2>
      <p className="text-lg text-stone-600 leading-relaxed">{service.description}</p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <li className="flex items-center gap-2 text-stone-600">
          <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
          <span className="text-sm">{service.f1}</span>
        </li>
        <li className="flex items-center gap-2 text-stone-600">
          <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
          <span className="text-sm">{service.f2}</span>
        </li>
        <li className="flex items-center gap-2 text-stone-600">
          <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
          <span className="text-sm">{service.f3}</span>
        </li>
        <li className="flex items-center gap-2 text-stone-600">
          <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
          <span className="text-sm">{service.f4}</span>
        </li>
        <li className="flex items-center gap-2 text-stone-600">
          <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
          <span className="text-sm">{service.f5}</span>
        </li>
      </ul>
      <Link to="/">
        <Button className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 font-semibold mt-4">
          Get Free Estimate
          <ArrowRight className="ml-2" size={18} />
        </Button>
      </Link>
    </div>
  </div>
);

const LOFT = {
  title: "Loft Conversions",
  description: "Transform your unused loft space into a stunning living area, bedroom, home office, or entertainment room.",
  image: "/images/projects/Loft1.JPG",
  price: "From £25,000",
  f1: "Full structural assessments", f2: "Planning permission support", f3: "Dormer transformations", f4: "En-suite installation", f5: "Skylights and windows"
};

const EXTENSIONS = {
  title: "House Extensions",
  description: "Expand your living space with a professionally built home extension.",
  image: "/images/projects/Extension1.JPG",
  price: "From £30,000",
  f1: "Single and double storey", f2: "Side and rear extensions", f3: "Garage builds", f4: "Structural steel work", f5: "Full project management"
};

const KITCHENS = {
  title: "Kitchen Fitting",
  description: "Create your dream kitchen with our professional fitting services.",
  image: "/images/projects/Kitchen1.JPG",
  price: "From £8,000",
  f1: "Complete installations", f2: "Custom cabinet fitting", f3: "Worktop installation", f4: "Appliance fitting", f5: "Kitchen islands"
};

const BATHROOMS = {
  title: "Bathroom Renovations",
  description: "Transform your bathroom into a relaxing retreat.",
  image: "/images/projects/Bathroom1.JPG",
  price: "From £5,000",
  f1: "Full renovations", f2: "Walk-in showers", f3: "Underfloor heating", f4: "Waterproof tiling", f5: "Vanity fitting"
};

const WARDROBES = {
  title: "Fitted Wardrobes",
  description: "Maximize your storage with custom-built fitted wardrobes.",
  image: "/images/projects/Wardrobe1.JPG",
  price: "From £2,500",
  f1: "Built-in wardrobes", f2: "Walk-in closets", f3: "Custom shelving", f4: "Media walls", f5: "Bedroom furniture"
};

const REFURBISHMENTS = {
  title: "Home Refurbishments",
  description: "Complete home renovations and refurbishments.",
  image: "/images/projects/Extension3.JPG",
  price: "From £20,000",
  f1: "Full property renovations", f2: "Flooring installation", f3: "Skirting and architrave", f4: "Partition walls", f5: "Door hanging"
};

const ServicesPage = () => {

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Building Services in Surrey & SW London"
        description="Extensions, loft conversions, kitchens & bathrooms across Surrey & SW London. Experienced, reliable builder. Free estimates. Call 07780 996775."
        keywords="loft conversion surrey, house extension sw london, kitchen fitter guildford, bathroom renovation, fitted wardrobes, home refurbishment"
        url="/services"
      />
      <BreadcrumbSchema items={BREADCRUMBS} />
      <Header />

      <section className="bg-gradient-to-br from-stone-100 to-stone-200 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">Our Services</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-6">
            Professional Carpentry and Construction
          </h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">
            From small joinery jobs to complete home transformations, we deliver exceptional craftsmanship across Surrey and SW London.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-24">
            <ServiceCard service={LOFT} reverse={false} />
            <ServiceCard service={EXTENSIONS} reverse={true} />
            <ServiceCard service={KITCHENS} reverse={false} />
            <ServiceCard service={BATHROOMS} reverse={true} />
            <ServiceCard service={WARDROBES} reverse={false} />
            <ServiceCard service={REFURBISHMENTS} reverse={true} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">Plus More</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Additional Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Door hanging</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Window fitting</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Decking and patios</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Fencing and gates</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Skirting boards</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Laminate flooring</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">Partition walls</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-6 border border-stone-100">
              <CheckCircle className="text-amber-700 mb-3" size={24} />
              <p className="font-semibold text-slate-900">General repairs</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Not Sure What You Need?</h2>
          <p className="text-lg text-stone-300 mb-8 max-w-2xl mx-auto">
            Get in touch and we will discuss your project requirements. We offer free site visits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button className="bg-amber-700 text-white hover:bg-amber-600 h-14 px-10 font-bold">
                Get Free Estimate
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="h-14 px-8 font-semibold border-2 border-white text-white hover:bg-white hover:text-slate-900">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
